import { fetchCategoryInfo } from "@/api/category";
import FilterByBtn from "@/components/actions/button/FilterBy";
import SortByBtn from "@/components/actions/button/SortBy";
import Breadcrumbs from "@/components/breadcrumbs";
import CartSidebarWrapper from "@/components/cart/sidebarWrapper";
import SortModal from "@/components/categories/modal/SortModal";
import NotFound from "@/components/error/NotFound";
import CustomSelect from "@/components/form/select/CustomSelect";
import Loader from "@/components/loader";
import AddToCartModal from "@/components/modal/AddToCartModal";
import Pagination from "@/components/pagination";
import Card from "@/components/product-card/large/Card";
import en from "@/localization/en.json";
import { getWishlistUpdatedProducts } from "@/utils/helpers/wishlist";
import MetaData from "@/components/seo/MetaData";
import _ from "lodash";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useFetchCategoryData } from "repo/category";
import { useFetchSortOptions } from "repo/search";
import { useFetchWishListInfo } from "repo/wishlist";
import { useSession } from "next-auth/react";
import { usePagination } from "hooks/usePagination";
import { useSort } from "hooks/useSort";
import {
  FilterMobileView,
  FourOFour,
  ProductFilters,
} from "@/components/index";
import { getPaginationResultText } from "@/utils/pagination";

function OffersPage({ offersData }) {
  const router = useRouter();
  const [searchInfo, setSearchInfo] = useState(offersData);
  const [rowData, setRowData] = useState(offersData?.attributes || []);
  const [offerProducts, setOfferProducts] = useState(offersData?.products);
  const { data: session } = useSession();
  const { getPaginateData } = usePagination();

  const { isFetching } = useFetchCategoryData(router.asPath, {
    onSuccess: (categoryData) => {
      setSearchInfo(categoryData);
      setOfferProducts(categoryData.products);
      setRowData(categoryData.attributes);
    },
    enabled: !!Object.keys(router.query).length,
  });

  const { data: sortOptionData = [] } = useFetchSortOptions();
  const { sortData, setSelectedSortValue } = useSort(sortOptionData);

  const { refetch } = useFetchWishListInfo({
    enabled: false,
    onSuccess: (wishlistData) => {
      if (!wishlistData.length) return;
      if (!_.isEmpty(offerProducts)) {
        setOfferProducts(
          getWishlistUpdatedProducts(wishlistData, offerProducts)
        );
      }
    },
  });

  useEffect(() => {
    if (session?.token) {
      refetch();
    }
  }, [session]);

  if (!searchInfo) {
    return <FourOFour />;
  }

  return (
    <>
      {isFetching && <Loader />}
      <MetaData {...offersData.metadata} schema={offersData.schema} />
      <div className="w-full pb-[1rem] sm:flex sm:justify-around">
        <div className="hidden sm:max-w-[15%] sm:w-[15%] sm:flex">
          <div className="sm:flex flex-col items-start w-full">
            <p className="text-grayD4 text-heading4 font-normal mt-[2rem]">
              {en["common.filter_by"]}
            </p>
            <div className="overflow-y-auto max-h-[131.25rem] customScrollBar pr-2">
              <ProductFilters filterData={rowData} prices={searchInfo.prices} />
            </div>
          </div>
        </div>

        <div className="w-full sm:w-[65%] mt-[1.375rem]">
          <div className="pl-[1.25rem] sm:pl-0">
            <Breadcrumbs breadcrumbs={[{ value: "Offers", link: "/offers" }]} />
          </div>

          <div className="sm:flex justify-between mt-[1.875rem] pl-[1.25rem] sm:pl-0 sm:pb-3">
            <div className="space-x-[0.75rem] sm:space-x-0  sm:space-y-[0.625rem] flex items-center sm:block">
              <p className="text-[#0A101A] text-[1.5rem] font-semibold">
                Offer Products
              </p>

              <p className="text-[#414E5F] text-[0.75rem]">
                {getPaginationResultText(
                  searchInfo?.meta,
                  en["common.results"]
                )}
              </p>
            </div>

            <div className="hidden sm:flex items-center">
              <label className="text-[#7B838F]">{en["common.sort_by"]}</label>
              <CustomSelect
                options={sortOptionData}
                onChange={(state) => sortData(state)}
                value={setSelectedSortValue}
              />
            </div>
          </div>

          <div className="pb-[2rem] pt-[3rem] sm:hidden">
            <div className="flex items-center justify-center space-x-[1rem]">
              <FilterByBtn />
              <SortByBtn />
            </div>
            <FilterMobileView filterData={rowData} prices={searchInfo.prices} />
            <SortModal
              sortData={sortOptionData}
              onChange={(data) => sortData({ value: data })}
              value={setSelectedSortValue}
            />
          </div>

          {offerProducts && offerProducts.length > 0 ? (
            <div className="w-full mx-auto grid grid-cols-2 gap-2 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4">
              {offerProducts.map((product) => (
                <Card product={product} key={product.slug} />
              ))}
            </div>
          ) : (
            <NotFound
              imageHolderClassName="w-[15rem] md:w-[20rem] h-[11.25rem] md:h-[15rem]"
              imageName="no-product-found.png"
              wrapperClassName="mx-auto mt-[4.375rem] mb-[8.43rem]"
              heading=""
              bodyText={en["common.no_product_found"]}
              bodyClassName="mt-[1.25rem] text-grayD3 text-body1 md:text-heading5 max-w-[23.5rem]"
            />
          )}
        </div>
      </div>

      <CartSidebarWrapper />
      <AddToCartModal />

      {searchInfo?.meta?.total > searchInfo?.meta?.per_page && (
        <div className="mb-[5.625rem]">
          <Pagination
            total={Math.ceil(
              searchInfo?.meta?.total / searchInfo?.meta?.per_page
            )}
            currentPage={searchInfo?.meta?.current_page - 1}
            getPaginateData={(data) => getPaginateData(data)}
          />
        </div>
      )}
    </>
  );
}

export async function getStaticProps() {
  const data = await fetchCategoryInfo(`/offers`);
  if (data) {
    return {
      props: {
        offersData: data.data,
      },
    };
  }

  return {
    props: { offersData: null },
  };
}

export default OffersPage;
